import {
  entityEdgesWithoutSubset,
  moveToFulfilled,
  moveToPendingIfNotFulfilled,
  objectWithoutKeys,
  update,
} from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  CuratedThreadListLoadAction,
  CuratedThreadListLoadFulfilledAction,
  GenerateCuratedThreadsAction,
} from '../../domains/curatedThreadList/actions.types';
import { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import { Thread, ThreadEdge } from '../types';

import { CuratedThreadListEdge, CuratedThreadLists } from './types';

export type CuratedThreadListsAction =
  | CuratedThreadListLoadAction
  | GenerateCuratedThreadsAction
  | DeleteMessageFulfilledAction;

const initialState: CuratedThreadLists = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithRejectedCuratedThreadList = (
  state: CuratedThreadLists,
  curatedThreadListId: string,
  error: unknown
) => ({
  ...state,
  fulfilled: {
    ...state.fulfilled,
    byId: objectWithoutKeys(state.fulfilled.byId, [curatedThreadListId]),
  },
  rejected: {
    ...state.rejected,
    byId: {
      ...state.rejected.byId,
      [curatedThreadListId]: error,
    },
  },
});

const stateWithFulfilledCuratedThreadList = (
  state: CuratedThreadLists,
  { curatedThreadListId, entities }: CuratedThreadListLoadFulfilledAction['payload']
) => {
  const existingCuratedThreadList = state.fulfilled.byId[curatedThreadListId];

  if (!existingCuratedThreadList) {
    return moveToFulfilled(state, {
      [curatedThreadListId]: { ...entities.curatedThreadLists[curatedThreadListId], dispatchCount: 1 },
    });
  }

  const updatedCuratedThreadList = {
    ...existingCuratedThreadList,
    dispatchCount: (existingCuratedThreadList.dispatchCount || 0) + 1,
  };

  return update(state, curatedThreadListId, updatedCuratedThreadList);
};

const removeThreadFromAllCuratedThreadLists = (
  state: CuratedThreadLists,
  { threadId }: DeleteMessageFulfilledAction['payload']
) =>
  Object.keys(state.fulfilled.byId).reduce(
    (updatedState, curatedThreadListId) =>
      removeThreadsFromCuratedThreadList(updatedState, [{ nodeId: threadId }], curatedThreadListId),
    state
  );

const removeThreadsFromCuratedThreadList = (
  state: CuratedThreadLists,
  threadEdges: ThreadEdge[],
  curatedThreadListId: string
) => {
  const curatedThreadList = state.fulfilled.byId[curatedThreadListId];

  if (!curatedThreadList || (curatedThreadList.edges && curatedThreadList.edges.length === 0)) {
    return state;
  }

  const nonNullCuratedThreadListEdges =
    curatedThreadList.edges?.filter((edge): edge is CuratedThreadListEdge => edge != null) ?? [];

  const otherThreadEdges = entityEdgesWithoutSubset<Thread>(nonNullCuratedThreadListEdges, threadEdges);

  if (otherThreadEdges.length === nonNullCuratedThreadListEdges.length) {
    return state;
  }

  const updatedCuratedthreadList = {
    ...curatedThreadList,
    edges: otherThreadEdges,
  };

  return update(state, curatedThreadListId, updatedCuratedthreadList);
};

const reducer: Reducer<CuratedThreadLists, CuratedThreadListsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CURATED_THREAD_LIST_LOAD.PENDING':
      return moveToPendingIfNotFulfilled(state, action.payload.curatedThreadListId);
    case 'CURATED_THREAD_LIST_LOAD.REJECTED':
      return stateWithRejectedCuratedThreadList(state, action.payload.curatedThreadListId, action.payload.error);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return stateWithFulfilledCuratedThreadList(state, action.payload);
    case 'MESSAGE.DELETE_FULFILLED':
      return removeThreadFromAllCuratedThreadLists(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
